/**
 * RelatedOrdersTable - Universal Related Orders display component
 * 
 * Part of the Unified Detail Page Design System v1.0
 * 
 * This component displays related orders with consistent columns everywhere:
 * - Order #
 * - Order Date
 * - Status (ops_status)
 * - Total Amount
 * - Invoice Status
 * 
 * Used in: Customer Detail, Contact Detail, Work Order Detail, etc.
 * 
 * Rules:
 * - Same columns everywhere
 * - Related panels show top 5 + "View All"
 * - One query per related list
 * - Pagination enforced
 * 
 * Enhanced Features (v1.1):
 * - Checkbox selection for multi-select
 * - Reorder button per row
 * - Combine Selected button for creating combined orders
 */
import React, { useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { ShoppingCart, Plus, ChevronRight, ExternalLink, RotateCcw, Layers } from 'lucide-react';

// Status badge colors - consistent everywhere
const getStatusBadge = (status) => {
  const statusMap = {
    // Ops Status
    'OPEN': 'bg-blue-100 text-blue-700',
    'IN_PROGRESS': 'bg-purple-100 text-purple-700',
    'COMPLETED': 'bg-green-100 text-green-700',
    'CANCELLED': 'bg-gray-100 text-gray-500',
    // Invoice Status
    'NONE': 'bg-gray-100 text-gray-500',
    'ISSUED': 'bg-blue-100 text-blue-700',
    'SENT': 'bg-indigo-100 text-indigo-700',
    'PAID': 'bg-green-100 text-green-700',
    'VOID': 'bg-red-100 text-red-700',
    'OVERDUE': 'bg-red-100 text-red-700',
    'PARTIAL': 'bg-yellow-100 text-yellow-700',
  };
  return statusMap[status] || 'bg-gray-100 text-gray-700';
};

// Format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * RelatedOrdersTable - Main component
 * 
 * @param {Array} orders - Array of order objects
 * @param {number} total - Total count of related orders
 * @param {function} onViewOrder - Callback when clicking an order row
 * @param {function} onViewAll - Callback for "View All" button
 * @param {function} onCreateOrder - Callback for "New Order" button
 * @param {function} onReorder - Callback for "Reorder" button (receives order_id)
 * @param {function} onCombineOrders - Callback for "Combine Orders" button (receives array of order_ids)
 * @param {boolean} showCreateButton - Whether to show create button
 * @param {boolean} showReorderActions - Whether to show reorder/combine actions
 * @param {boolean} loading - Loading state
 * @param {number} maxItems - Max items to show (default 5)
 * @param {string} emptyMessage - Message when no orders
 * @param {string} testIdPrefix - Prefix for test IDs
 */
const RelatedOrdersTable = ({
  orders = [],
  total = 0,
  onViewOrder,
  onViewAll,
  onCreateOrder,
  onReorder,
  onCombineOrders,
  showCreateButton = true,
  showReorderActions = false,
  loading = false,
  maxItems = 5,
  emptyMessage = 'No orders yet',
  testIdPrefix = 'related-orders',
  className = '',
}) => {
  const displayOrders = orders.slice(0, maxItems);
  const hasMore = total > maxItems;
  
  // Selection state for combining orders
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  
  const handleSelectOrder = (orderId, checked) => {
    if (checked) {
      setSelectedOrderIds(prev => [...prev, orderId]);
    } else {
      setSelectedOrderIds(prev => prev.filter(id => id !== orderId));
    }
  };
  
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedOrderIds(displayOrders.map(o => o.order_id || o.id));
    } else {
      setSelectedOrderIds([]);
    }
  };
  
  const handleCombineSelected = () => {
    if (selectedOrderIds.length > 0 && onCombineOrders) {
      onCombineOrders(selectedOrderIds);
      setSelectedOrderIds([]); // Clear selection after action
    }
  };
  
  const allSelected = displayOrders.length > 0 && 
    displayOrders.every(o => selectedOrderIds.includes(o.order_id || o.id));
  
  return (
    <div className={`bg-white rounded-lg border ${className}`} data-testid={`${testIdPrefix}-section`}>
      {/* Header */}
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShoppingCart className="w-4 h-4 text-blue-600" />
          <h3 className="font-semibold">Orders</h3>
          {total > 0 && <Badge variant="outline" className="text-xs">{total}</Badge>}
        </div>
        <div className="flex items-center gap-2">
          {/* Combine Selected Button */}
          {showReorderActions && selectedOrderIds.length > 1 && onCombineOrders && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleCombineSelected}
              className="h-7 px-2 bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
              data-testid={`${testIdPrefix}-combine-btn`}
            >
              <Layers className="w-4 h-4 mr-1" />
              Combine {selectedOrderIds.length} Orders
            </Button>
          )}
          {showCreateButton && onCreateOrder && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onCreateOrder}
              className="h-7 px-2"
              data-testid={`${testIdPrefix}-create-btn`}
            >
              <Plus className="w-4 h-4 mr-1" />
              New Order
            </Button>
          )}
          {hasMore && onViewAll && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onViewAll}
              className="h-7 px-2"
              data-testid={`${testIdPrefix}-view-all-btn`}
            >
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="text-center py-8 text-gray-400">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            Loading orders...
          </div>
        ) : displayOrders.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-gray-500 border-b">
                <tr>
                  {showReorderActions && onCombineOrders && (
                    <th className="pb-2 pr-2 w-8">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={handleSelectAll}
                        data-testid={`${testIdPrefix}-select-all`}
                      />
                    </th>
                  )}
                  <th className="pb-2 font-medium">Order #</th>
                  <th className="pb-2 font-medium">Order Date</th>
                  <th className="pb-2 font-medium">Status</th>
                  <th className="pb-2 font-medium text-right">Total</th>
                  <th className="pb-2 font-medium">Invoice</th>
                  {showReorderActions && <th className="pb-2 font-medium w-20">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y">
                {displayOrders.map(order => {
                  const orderId = order.order_id || order.id;
                  const isSelected = selectedOrderIds.includes(orderId);
                  
                  return (
                    <tr
                      key={orderId}
                      className={`hover:bg-gray-50 group ${isSelected ? 'bg-amber-50' : ''}`}
                      data-testid={`${testIdPrefix}-row-${orderId}`}
                    >
                      {showReorderActions && onCombineOrders && (
                        <td className="py-3 pr-2">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) => handleSelectOrder(orderId, checked)}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`${testIdPrefix}-select-${orderId}`}
                          />
                        </td>
                      )}
                      <td 
                        className="py-3 cursor-pointer"
                        onClick={() => onViewOrder?.(orderId)}
                      >
                        <span className="font-medium text-blue-600 group-hover:underline flex items-center gap-1">
                          {orderId}
                          <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </span>
                      </td>
                      <td className="py-3 text-gray-600">
                        {formatDate(order.created_at)}
                      </td>
                      <td className="py-3">
                        <Badge className={getStatusBadge(order.ops_status)}>
                          {order.ops_status || 'OPEN'}
                        </Badge>
                      </td>
                      <td className="py-3 text-right font-medium">
                        {formatCurrency(order.total)}
                      </td>
                      <td className="py-3">
                        <Badge className={getStatusBadge(order.invoice_status)}>
                          {order.invoice_status || 'NOT ISSUED'}
                        </Badge>
                      </td>
                      {showReorderActions && (
                        <td className="py-3">
                          {onReorder && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                onReorder(orderId);
                              }}
                              title="Create new order with same items"
                              data-testid={`${testIdPrefix}-reorder-${orderId}`}
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>{emptyMessage}</p>
            {showCreateButton && onCreateOrder && (
              <Button
                variant="outline"
                size="sm"
                onClick={onCreateOrder}
                className="mt-4"
                data-testid={`${testIdPrefix}-create-empty-btn`}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Order
              </Button>
            )}
          </div>
        )}
      </div>
      
      {/* Footer with View All (alternative position) */}
      {displayOrders.length > 0 && hasMore && onViewAll && (
        <div className="px-4 py-2 border-t bg-gray-50">
          <button
            onClick={onViewAll}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            data-testid={`${testIdPrefix}-view-all-footer`}
          >
            View All {total} Orders
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * RelatedOrdersCompact - Compact version for sidebars (3 items max)
 */
export const RelatedOrdersCompact = ({
  orders = [],
  total = 0,
  onViewOrder,
  onViewAll,
  onCreateOrder,
  showCreateButton = true,
  loading = false,
  testIdPrefix = 'related-orders-compact',
}) => {
  const displayOrders = orders.slice(0, 3);
  
  return (
    <div className="bg-white rounded-lg border p-4" data-testid={`${testIdPrefix}-section`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold flex items-center gap-2">
          <ShoppingCart className="w-4 h-4 text-blue-600" />
          Orders
          {total > 0 && <Badge variant="outline" className="text-xs">{total}</Badge>}
        </h3>
        {showCreateButton && onCreateOrder && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onCreateOrder}
            className="h-7 px-2"
            data-testid={`${testIdPrefix}-create-btn`}
          >
            <Plus className="w-4 h-4" />
          </Button>
        )}
      </div>
      
      {/* Items */}
      {loading ? (
        <div className="text-center py-4 text-gray-400 text-sm">Loading...</div>
      ) : displayOrders.length > 0 ? (
        <div className="space-y-2">
          {displayOrders.map(order => (
            <div
              key={order.order_id || order.id}
              className="p-2 rounded hover:bg-gray-50 cursor-pointer text-sm flex items-center justify-between"
              onClick={() => onViewOrder?.(order.order_id || order.id)}
              data-testid={`${testIdPrefix}-item-${order.order_id || order.id}`}
            >
              <div>
                <p className="font-medium text-blue-600">{order.order_id || order.id}</p>
                <p className="text-gray-500 text-xs">
                  {formatDate(order.created_at)} • {formatCurrency(order.total)}
                </p>
              </div>
              <Badge className={`${getStatusBadge(order.ops_status)} text-xs`}>
                {order.ops_status || 'OPEN'}
              </Badge>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-400 text-sm text-center py-4">{loading ? 'Loading...' : 'No orders'}</p>
      )}
      
      {/* View All */}
      {total > 3 && onViewAll && (
        <Button
          variant="link"
          size="sm"
          className="w-full mt-2"
          onClick={onViewAll}
          data-testid={`${testIdPrefix}-view-all-btn`}
        >
          View All ({total})
        </Button>
      )}
    </div>
  );
};

export default RelatedOrdersTable;
